import React from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";
import { Button } from "@gettactic/components/src/primitives/button";
import { RadioInput } from "@gettactic/components/src/form/RadioInput/RadioInput";
import { CalendarInputInput } from "@gettactic/components/src/form/calendar/CalendarInput/CalendarInput";
import { TimeSelect } from "@gettactic/components/src/form/TimeSelect/TimeSelect";
import type {
  MapSaveMode,
  MapSaveOptions
} from "../../contexts/MapCanvasContext/MapCanvasContext.types";

interface SaveMapDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (options: MapSaveOptions) => void;
  isSaving?: boolean;
  /** Modes to offer; all three by default. */
  modes?: MapSaveMode[];
}

const ALL_SAVE_MODES: MapSaveMode[] = ["draft", "activate-now", "schedule"];

function buildActiveFromUTC(date: Date, time: string): string {
  const [timePart, period] = time.split(" ");
  const [hoursStr, minutes] = timePart.split(":");
  let hours = Number(hoursStr);
  if (period === "PM" && hours !== 12) hours += 12;
  if (period === "AM" && hours === 12) hours = 0;

  const result = new Date(date);
  result.setHours(hours, Number(minutes), 0, 0);
  return result.toISOString();
}

export const SaveMapDialog: React.FC<SaveMapDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isSaving,
  modes = ALL_SAVE_MODES
}) => {
  const [mode, setMode] = React.useState<MapSaveMode>(
    modes.includes("activate-now") ? "activate-now" : modes[0]
  );
  const [scheduleDate, setScheduleDate] = React.useState<Date>(new Date());
  const [scheduleTime, setScheduleTime] = React.useState<string>("09:00 AM");

  const handleConfirm = () => {
    if (mode === "draft") {
      onConfirm({ mode: "draft" });
    } else if (mode === "activate-now") {
      onConfirm({ mode: "activate-now" });
    } else {
      onConfirm({
        mode: "schedule",
        activeFrom: buildActiveFromUTC(scheduleDate, scheduleTime)
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save Map</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          {modes.includes("draft") && (
            <RadioInput
              label="Save as draft"
              description="Save without activating. The map won't be visible to users."
              radioPosition="left"
              checked={mode === "draft"}
              onChange={() => setMode("draft")}
              disableOutline
            />
          )}
          {modes.includes("activate-now") && (
            <RadioInput
              label="Save and activate now"
              description="Save and make this map version active immediately."
              radioPosition="left"
              checked={mode === "activate-now"}
              onChange={() => setMode("activate-now")}
              disableOutline
            />
          )}
          {modes.includes("schedule") && (
            <RadioInput
              label="Schedule activation"
              description="Save and activate at a specific date and time."
              radioPosition="left"
              checked={mode === "schedule"}
              onChange={() => setMode("schedule")}
              disableOutline
            />
          )}
          {mode === "schedule" && (
            <div className="ml-8 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <CalendarInputInput
                    mode="single"
                    selectedDate={scheduleDate}
                    onSelect={(date) => {
                      if (date instanceof Date) {
                        setScheduleDate(date);
                      }
                    }}
                    fromDate={new Date()}
                    variant="outlined"
                  />
                </div>
                <div className="w-36">
                  <TimeSelect
                    value={scheduleTime}
                    onSelect={(value) => {
                      if (value) setScheduleTime(value);
                    }}
                    placeholder="Time"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
